// "Next goal" hint: the first rule in common/data/goals.json that matches what the player has and hasn't done.
// Pure and client-evaluated (no protocol change); the server never reads it.
import goalsJ from '../data/goals.json';
import { BOSSES, CHALLENGES, CRED_JOBS, PROGRESSION } from './data';

export type GoalWhen = 'noCred' | 'points' | 'bossHunt' | 'nextUnlock' | 'always';
const WHENS: GoalWhen[] = ['noCred', 'points', 'bossHunt', 'nextUnlock', 'always'];
export interface GoalRule { id: string; when: GoalWhen; text: string; minCred?: number; point?: 'kiosk' }
export const GOALS = goalsJ as unknown as { goalCard: { title: string; lines: string[] }; goals: GoalRule[] };

export interface GoalState { cred: number; perkPoints: number; attrPoints: number; inJob: boolean; bossDone: boolean }
export interface Goal { id: string; text: string; point?: 'kiosk' }

/** Cred thresholds that open something, ascending: the cred jobs, then Boss Hunt tiers 2 and up. */
export function unlockList(): { n: number; name: string }[] {
  const out: { n: number; name: string }[] = [];
  for (const k of CRED_JOBS) { const n = PROGRESSION.credUnlocks[k]; if (n) out.push({ n, name: CHALLENGES[k].name }); }
  for (const t of Object.values(BOSSES.tiers)) out.push({ n: t.credNeeded, name: `Boss Hunt: ${t.name}` });
  return out.sort((a, b) => a.n - b.n);
}

/** The goal to show, or null while a job is running (the job panel has its own objective). */
export function pickGoal(s: GoalState): Goal | null {
  if (s.inJob) return null;
  for (const r of GOALS.goals) {
    let text = r.text;
    switch (r.when) {
      case 'noCred': if (s.cred > 0) continue; break;
      case 'points': if (s.perkPoints + s.attrPoints <= 0) continue; break;
      case 'bossHunt': if (s.bossDone || s.cred < (r.minCred ?? 0)) continue; break;
      case 'nextUnlock': {
        const nx = unlockList().find(u => u.n > s.cred); if (!nx) continue;
        text = text.replace('{n}', String(nx.n)).replace('{name}', nx.name); break;
      }
      case 'always': break;
    }
    return { id: r.id, text, point: r.point };
  }
  return null;
}

export function validateGoals(e: string[]) {
  const c = GOALS.goalCard;
  if (!c || !c.title || !Array.isArray(c.lines) || c.lines.length < 1 || c.lines.length > 4 || c.lines.some(l => !l)) e.push('goals.goalCard needs a title and 1-4 lines');
  const ids = new Set<string>();
  if (!GOALS.goals?.length) { e.push('goals.goals is empty'); return; }
  for (const g of GOALS.goals) {
    const p = `goals.${g.id}`;
    if (!g.id || ids.has(g.id)) e.push(`${p} id missing or duplicate`);
    ids.add(g.id);
    if (!WHENS.includes(g.when)) e.push(`${p} unknown when "${g.when}"`);
    if (!g.text || g.text.length > 60) e.push(`${p} text needs 1-60 characters`);
    if (g.point !== undefined && g.point !== 'kiosk') e.push(`${p} point must be "kiosk"`);
    if (g.minCred !== undefined && !(g.minCred >= 0)) e.push(`${p} minCred must be >= 0`);
    if (g.when === 'nextUnlock' && !(g.text.includes('{n}') && g.text.includes('{name}'))) e.push(`${p} nextUnlock text needs {n} and {name}`);
  }
  if (GOALS.goals[GOALS.goals.length - 1].when !== 'always') e.push('goals.goals must end with an "always" goal');
}
